import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import ServiceCard from "@/components/cards/ServiceCard";
import BaseCard from "@/components/cards/BaseCard";
import { COMPANY } from "@/data/company";
import { ALL_SERVICES, REGIONS, INDUSTRIES } from "@/data/nav";
import { trackPhoneClick } from "@/lib/track";

/**
 * IndustryTemplate — data-driven layout for /industries/:slug pages.
 * Consumes an entry from INDUSTRY_PAGES (see data/industries.js).
 */
const IndustryTemplate = ({ data }) => {
  const services = data.servicesFocus
    .map((slug) => ALL_SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  const siblingIndustries = INDUSTRIES.filter((i) => i.slug !== data.slug).slice(0, 3);
  const featuredRegions = REGIONS.filter((r) => r.slug !== "north-west-england").slice(0, 3);

  return (
    <div data-testid={`industry-page-${data.slug}`}>
      <Seo
        title={data.seo.title}
        description={data.seo.description}
        path={`/industries/${data.slug}`}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <PhotoBackdrop src={data.heroImage} position={data.heroPosition} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">Industries · {data.name}</p>
          <h1
            className="font-display mt-6 text-white max-w-4xl"
            style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1 }}
            data-testid="industry-h1"
          >
            {data.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.subhead}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.phone.tel}
              onClick={() => trackPhoneClick(`industry_${data.slug}_hero`)}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="industry-hero-call"
            >
              <Phone size={16} /> Talk to an engineer
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="industry-hero-enquire"
            >
              Send an enquiry <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <Breadcrumb
        items={[
          { label: "Industries", to: "/industries" },
          { label: data.name },
        ]}
      />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">The sector, in plain terms</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
          >
            How we support {data.name.toLowerCase()} sites.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)] whitespace-pre-line">
            {data.intro}
          </p>
        </div>
      </section>

      {/* TYPICAL REQUIREMENTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Typical requirements</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          What we typically get asked for.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.typicalRequirements.map((item, i) => (
            <div
              key={i}
              className="glass-card rounded-lg p-5 flex gap-3"
              data-testid={`industry-req-${i}`}
            >
              <Check
                size={18}
                className="mt-1 shrink-0 text-[color:var(--lt-blue-bright)]"
              />
              <p className="text-[14.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE SUPPORT */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">How we work with the sector</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          Four things we do differently.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.howWeSupport.map((block, i) => (
            <div
              key={i}
              className="glass-card rounded-xl p-6 lg:p-8"
              data-testid={`industry-support-${i}`}
            >
              <h3 className="font-display text-white text-[20px] leading-tight">
                {block.title}
              </h3>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
                {block.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* RELEVANT SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Services relevant to {data.name}</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          Most-requested services in this sector.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s) => (
            <ServiceCard
              key={s.slug}
              slug={s.slug}
              name={s.name}
              blurb={s.blurb}
              categoryName={s.categoryName}
            />
          ))}
        </div>
        <div className="mt-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[13.5px] text-[color:var(--lt-blue-bright)] font-semibold"
          >
            View all services <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* COVERAGE STRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="glass-card rounded-xl p-8 lg:p-10">
          <p className="eyebrow">Coverage</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(24px, 2.8vw, 34px)", lineHeight: 1.05 }}
          >
            Where we cover.
          </h2>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.responsePromise}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {featuredRegions.map((r) => (
              <Link
                key={r.slug}
                to={`/coverage/${r.slug}`}
                className="btn-ghost rounded-full px-4 py-2 text-[13px] font-semibold"
                data-testid={`industry-region-chip-${r.slug}`}
              >
                {r.name}
              </Link>
            ))}
            <Link
              to="/coverage/north-west-england"
              className="btn-ghost rounded-full px-4 py-2 text-[13px] font-semibold"
              data-testid="industry-region-chip-north-west"
            >
              North West England
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">{data.name} FAQ</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
            >
              Common questions from the sector.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={data.faq} testIdPrefix={`industry-faq-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <CTABanner
        variant="standard"
        title={`Working in ${data.name.toLowerCase()}? Let's talk about the scope honestly.`}
        body="Send us a short brief of what you're trying to solve. We'll come back with an honest view on whether we're the right fit — and, if we are, a fixed-price way forward."
        phoneLocation={`industry_${data.slug}_bottom`}
      />

      {/* RELATED — other industries */}
      <RelatedBlock
        title="Other sectors we support"
        groups={[
          {
            eyebrow: "Related industries",
            items: siblingIndustries.map((i) => (
              <BaseCard
                key={i.slug}
                to={`/industries/${i.slug}`}
                eyebrow="Industry"
                title={i.name}
                blurb={i.blurb}
                testid={`industry-related-${i.slug}`}
              />
            )),
          },
        ]}
      />
    </div>
  );
};

export default IndustryTemplate;
